import { Button, Grid, TextField, Theme, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import { Dispatch, FC, SetStateAction, useCallback, useState } from 'react';

import { SetShowDialog, SetShowSnackbar } from '../../../types';
import { BaseTierRate, OrderableTier, SequencingCountAndRate, SequencingTier } from '../../../types/leases';
import { SimpleModal } from '../../modals';
import { AddBaseTierModal } from './addBaseTierModal';
import { AddSequencingTierModal } from './addSequencingTierModal';
import { ReorderTiersModal } from './reorderTiersModal';

type SequencingHeaderProps = {
	baseTierRates: BaseTierRate[];
	countsAndRates: SequencingCountAndRate[];
	inBulkSequencingMode: boolean;
	loading: boolean;
	subscriptionId: string;
	theme: Theme;
	tiers: SequencingTier[];
	setNumber: number;
	maxSetNumber: number;
	handleBulkLockOrUnlock: (isLocked: boolean) => Promise<void>;
	handleBulkResequenceCancel: () => void;
	handleBulkResequencing: (startingSequenceNumber: number) => void;
	handleClose: () => void;
	reorderTiers: (updatedTiers: OrderableTier[]) => Promise<void>;
	pollSequencingTiers: () => Promise<void>;
	setInBulkSequencingMode: Dispatch<SetStateAction<boolean>>;
	setShowDialog: SetShowDialog;
	setShowSnackbar: SetShowSnackbar;
};

const bulkLockModalContent = `Selecting 'Continue' will lock all Parkers into their current tiers. This will pause sequencing for this Subscription, and no sequencing actions will occur while all Parkers are locked.`;

const bulkUnlockModalContent = `Selecting 'Continue' will unlock all Parkers into their current tiers. This will resume sequencing actions for this Subscription.`;

export const SequencingHeader: FC<SequencingHeaderProps> = ({
	baseTierRates,
	countsAndRates,
	inBulkSequencingMode,
	loading,
	subscriptionId,
	theme,
	tiers,
	setNumber,
	maxSetNumber,
	handleBulkLockOrUnlock,
	handleBulkResequenceCancel,
	handleBulkResequencing,
	handleClose,
	reorderTiers,
	pollSequencingTiers,
	setInBulkSequencingMode,
	setShowDialog,
	setShowSnackbar,
}) => {
	const [startingSequenceNumber, setStartingSequencingNumber] = useState<number>();

	const handleBulkLocking = useCallback(
		(isLocked: boolean) => {
			setShowDialog(
				<SimpleModal
					content={isLocked ? bulkLockModalContent : bulkUnlockModalContent}
					confirmText='Continue'
					dismissText='Cancel'
					handleDismiss={() => setShowDialog(null)}
					handleConfirm={() => handleBulkLockOrUnlock(isLocked)}
					contentStyle={{
						fontSize: 'large',
						display: 'flex',
						justifyContent: 'center',
						margin: '3em',
						maxWidth: '30em',
					}}
				/>,
			);
		},
		[setShowDialog, handleBulkLockOrUnlock],
	);

	if (inBulkSequencingMode) {
		return (
			<Grid container width='100%' sx={{ marginBottom: '10px' }}>
				<Grid item sm={12} justifyContent='flex-start'>
					<Stack direction='row' justifyContent='flex-start' alignItems='center'>
						<Typography variant='body1' justifyContent='center'>
							Start at sequence number:
						</Typography>
						<TextField
							sx={{ maxWidth: '75px', marginRight: '15px', marginLeft: '15px' }}
							size='small'
							type='number'
							onChange={(e) => setStartingSequencingNumber(Number(e.target.value))}
							onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
						/>
						<Button variant='outlined' onClick={handleBulkResequenceCancel}>
							Cancel
						</Button>
						<Button
							sx={{ marginLeft: '15px' }}
							variant='contained'
							onClick={() => handleBulkResequencing(startingSequenceNumber)}
						>
							Save
						</Button>
					</Stack>
				</Grid>
			</Grid>
		);
	}

	return (
		<Grid container width='100%' rowSpacing={1} sx={{ marginBottom: '10px' }}>
			<Grid item>
				<Button
					disabled={loading}
					onClick={() => {
						setShowDialog(
							<AddSequencingTierModal
								subscriptionId={subscriptionId}
								countsAndRates={countsAndRates}
								tiers={tiers}
								setNumber={setNumber}
								theme={theme}
								setShowSnackbar={setShowSnackbar}
								handleClose={handleClose}
								pollSequencingTiers={pollSequencingTiers}
							/>,
						);
					}}
				>
					Add Tier
				</Button>
			</Grid>
			<Grid item>
				<Button
					disabled={loading}
					onClick={() => {
						setShowDialog(
							<AddBaseTierModal
								subscriptionId={subscriptionId}
								rates={baseTierRates}
								theme={theme}
								setNumber={setNumber}
								setShowSnackbar={setShowSnackbar}
								handleClose={handleClose}
								pollSequencingTiers={pollSequencingTiers}
							/>,
						);
					}}
				>
					Add Base Tier
				</Button>
			</Grid>
			<Grid item>
				<Button
					disabled={loading}
					onClick={() => {
						setShowDialog(
							<ReorderTiersModal
								theme={theme}
								tiers={tiers}
								handleClose={handleClose}
								handleSave={reorderTiers}
							/>,
						);
					}}
				>
					Reorder Tiers
				</Button>
			</Grid>
			<Grid item>
				<Button disabled={loading} onClick={() => setInBulkSequencingMode(true)}>
					Bulk Resequence
				</Button>
			</Grid>
			<Grid item>
				<Button disabled={loading} onClick={() => handleBulkLocking(true)}>
					Lock All Parkers
				</Button>
			</Grid>
			<Grid item>
				<Button disabled={loading} onClick={() => handleBulkLocking(false)}>
					Unlock All Parkers
				</Button>
			</Grid>
			<Grid item>
				<Button
					disabled={loading}
					onClick={() => {
						setShowDialog(
							<AddSequencingTierModal
								subscriptionId={subscriptionId}
								countsAndRates={countsAndRates}
								tiers={[]}
								setNumber={maxSetNumber + 1}
								theme={theme}
								setShowSnackbar={setShowSnackbar}
								handleClose={handleClose}
								pollSequencingTiers={pollSequencingTiers}
							/>,
						);
					}}
				>
					Add Tier Set
				</Button>
			</Grid>
		</Grid>
	);
};
